// ── Assessment calculation core (pure, no DB) ────────────────────────────────
// Structure:  Term → Exam (Opener, Mid-Term, End-Term…) → Subject → Papers/components
//
//   • Within one exam a subject's papers combine either as a plain
//     Σraw / Σmax, or — when the setup defines paper `groups` for that exam —
//     as a weighted blend of groups, e.g. (PP1+PP2)/160 × 60% + PP3/40 × 40%.
//   • A term's subject result is the weighted average of its exams' percentages
//     (exam weights default to equal). An exam only counts (and is only graded)
//     once ALL its papers are marked; incomplete exams are left out.
//
// A "legacy" line/component has no examId; those all fall into the single
// default exam (key ''), which reproduces the old flat Σraw/Σmax exactly.

const r2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
const examKey = (id) => (id ? String(id) : '');
const isEntered = (l) => l.score !== null && l.score !== undefined && !Number.isNaN(l.score);

// One exam's result from its score lines. `groups` = [{ name, weight }] for THIS exam.
// Returns null until EVERY paper of the exam has a mark — an exam is only graded
// once all its components are done.
function examResult(lines, groups = []) {
  if (!lines.length || !lines.every(isEntered)) return null;
  const raw = lines.reduce((s, l) => s + (isEntered(l) ? l.score : 0), 0);
  const max = lines.reduce((s, l) => s + (l.maxScore || 0), 0);

  let percentage;
  const active = (groups || []).filter(g => Number(g.weight) > 0);
  if (active.length) {
    const named = new Set(active.map(g => g.name));
    const groupWeightSum = active.reduce((s, g) => s + Number(g.weight), 0);
    // Papers not assigned to any group share whatever weight is left (if any).
    const leftover = Math.max(0, 100 - groupWeightSum);
    const buckets = active.map(g => ({
      weight: Number(g.weight), lines: lines.filter(l => l.group === g.name),
    }));
    const ungrouped = lines.filter(l => !l.group || !named.has(l.group));
    if (ungrouped.length && leftover > 0) buckets.push({ weight: leftover, lines: ungrouped });

    let num = 0; let den = 0;
    for (const b of buckets) {
      const bMax = b.lines.reduce((s, l) => s + (l.maxScore || 0), 0);
      if (!bMax) continue;
      const bRaw = b.lines.reduce((s, l) => s + (isEntered(l) ? l.score : 0), 0);
      num += (bRaw / bMax) * b.weight; den += b.weight;
    }
    percentage = den ? (num / den) * 100 : 0;
  } else {
    percentage = max > 0 ? (raw / max) * 100 : 0;
  }
  return { raw, max, percentage: r2(percentage) };
}

// Recompute a learner's score document from its lines.
//   lines : doc.scores  ({ examId, examName, componentName, group, score, maxScore })
//   exams : [{ _id, name, weight, sequence }]  — AssessmentExam rows for this term/stage
//   groups: setup.groups [{ examId, name, weight }]
// Returns { examResults, totalRaw, totalMax, percentage }.
function computeScoreDoc(lines, exams = [], groups = []) {
  const byExam = new Map();
  for (const l of lines) {
    const k = examKey(l.examId);
    if (!byExam.has(k)) byExam.set(k, []);
    byExam.get(k).push(l);
  }
  const examMeta = new Map(exams.map(e => [examKey(e._id), e]));

  const examResults = [];
  for (const [k, exLines] of byExam) {
    const meta = examMeta.get(k);
    const exGroups = groups.filter(g => examKey(g.examId) === k);
    const res = examResult(exLines, exGroups);
    examResults.push({
      examId: k || null,
      examName: meta?.name || exLines[0]?.examName || 'Term Assessment',
      weight: meta && Number(meta.weight) > 0 ? Number(meta.weight) : 0,
      sequence: meta?.sequence ?? 0,
      entered: !!res,
      pending: !res && exLines.some(isEntered),   // some, but not all, papers marked
      raw: res ? res.raw : 0,
      max: exLines.reduce((s, l) => s + (l.maxScore || 0), 0),
      percentage: res ? res.percentage : 0,
    });
  }
  examResults.sort((a, b) => (a.sequence - b.sequence) || a.examName.localeCompare(b.examName));

  const sat = examResults.filter(e => e.entered);
  const anyWeight = sat.some(e => e.weight > 0);
  let percentage = 0;
  if (sat.length) {
    const w = (e) => (anyWeight ? e.weight : 1);
    const den = sat.reduce((s, e) => s + w(e), 0);
    percentage = den ? sat.reduce((s, e) => s + e.percentage * w(e), 0) / den : 0;
  }
  return {
    examResults,
    totalRaw: lines.reduce((s, l) => s + (isEntered(l) ? l.score : 0), 0),
    totalMax: lines.reduce((s, l) => s + (l.maxScore || 0), 0),
    percentage: r2(percentage),
    graded: sat.length > 0,   // false until at least one exam is fully marked
  };
}

// Project score docs onto ONE exam so existing report code (which reads
// `.percentage`) works unchanged per exam. Docs where that exam has no marks are
// dropped. examId '' / 'term' / undefined → the termly rollup (docs untouched).
function projectToExam(scoreDocs, examId) {
  if (!examId || examId === 'term') return scoreDocs;
  const k = examKey(examId);
  const out = [];
  for (const d of scoreDocs) {
    const er = (d.examResults || []).find(e => examKey(e.examId) === k && e.entered);
    if (!er) continue;
    out.push({
      ...d,
      percentage: er.percentage,
      totalRaw: er.raw,
      totalMax: er.max,
      scores: (d.scores || []).filter(l => examKey(l.examId) === k),
    });
  }
  return out;
}

export { examResult, computeScoreDoc, projectToExam, examKey };
